import {
    EntityType,
    LinkedContentObjectContract,
    LinkedContentObjectsContract,
} from '@polaris-customerweb/sharedcomponents.core';
import {
    Geofence,
    PlannedRide,
    TrackedRide,
    UserContentItem,
    Waypoint,
} from '@polaris-customerweb/sharedcomponents.user-content';
import { cloneDeep } from 'lodash-es';
import { Observable } from 'rxjs';
import { Collection } from './collection';
import { ContentCollection } from './content-collection';
import { RideEvent } from './ride-event';

export class Content {
    private _collection: Collection;
    private _contentSeedFactory: (
        id: string
    ) => Observable<ContentCollection> | undefined;
    private _event: RideEvent;
    private _geofence: Geofence;
    private _plannedRide: PlannedRide;
    private _trackedRide: TrackedRide;
    private _waypoint: Waypoint;
    private contract: LinkedContentObjectContract;

    constructor(
        contract: LinkedContentObjectContract = {},
        contentSeedFactory: (
            id: string
        ) => Observable<ContentCollection> = () =>
            new Observable<ContentCollection>()
    ) {
        this.contract = cloneDeep(contract);
        this._contentSeedFactory = contentSeedFactory;
    }

    get collection(): Collection {
        if (!this._collection && this.contract.collection) {
            let contentSeed$ = new Observable<ContentCollection>();
            if (this._contentSeedFactory) {
                contentSeed$ = this._contentSeedFactory(
                    this.contract.collection.id || ''
                ) as Observable<ContentCollection>;
            }

            this._collection = new Collection(
                this.contract.collection,
                contentSeed$
            );
        }

        return this._collection;
    }

    get contentItem(): UserContentItem {
        return (
            this.trackedRide ||
            this.plannedRide ||
            this.waypoint ||
            this.geofence ||
            this.collection ||
            this.event
        );
    }

    get event(): RideEvent {
        if (!this._event && this.contract.event) {
            this._event = new RideEvent(this.contract.event);
        }

        return this._event;
    }

    get geofence(): Geofence {
        if (!this._geofence && this.contract.geofence) {
            this._geofence = new Geofence(this.contract.geofence);
        }

        return this._geofence;
    }

    get linkedType(): EntityType {
        if (this.collection) {
            return 'Collection';
        } else if (this.event) {
            return 'Event';
        } else if (this.geofence) {
            return 'Geofence';
        } else if (this.plannedRide) {
            return 'PlannedRide';
        } else if (this.trackedRide) {
            return 'TrackedRide';
        } else if (this.waypoint) {
            return 'Waypoint';
        } else {
            return 'Unknown';
        }
    }

    get plannedRide(): PlannedRide {
        if (!this._plannedRide && this.contract.plannedRide) {
            this._plannedRide = new PlannedRide(this.contract.plannedRide);
        }

        return this._plannedRide;
    }

    get totalDistanceInMeters(): number {
        if (this.plannedRide) {
            return this.plannedRide.ride.totalLengthInMeters;
        } else if (this.trackedRide) {
            return this.trackedRide.totalLengthMeters;
        } else if (this.collection) {
            return this.collection.content?.totalDistanceInMeters || 0;
        } else if (this.event) {
            return this.event.distanceInMeters || 0;
        } else {
            return 0;
        }
    }

    get totalDurationInSeconds(): number {
        if (this.plannedRide) {
            return this.plannedRide.ride.totalDurationInSeconds;
        } else if (this.trackedRide) {
            return this.trackedRide.totalDurationSeconds;
        } else if (this.collection) {
            return this.collection.content?.totalDurationInSeconds || 0;
        } else if (this.event) {
            return this.event.durationInSeconds || 0;
        } else {
            return 0;
        }
    }

    get trackedRide(): TrackedRide {
        if (!this._trackedRide && this.contract.trackedRide) {
            this._trackedRide = new TrackedRide(this.contract.trackedRide);
        }

        return this._trackedRide;
    }

    get waypoint(): Waypoint {
        if (!this._waypoint && this.contract.waypoint) {
            this._waypoint = new Waypoint(this.contract.waypoint);
        }

        return this._waypoint;
    }

    static fromContentCollectionContract(
        contract: LinkedContentObjectsContract
    ): Content {
        const contentContract: LinkedContentObjectContract = {};

        if (contract.collections && contract.collections[0]) {
            contentContract.collection = contract.collections[0];
        } else if (contract.events && contract.events[0]) {
            contentContract.event = contract.events[0];
        } else if (contract.geofences && contract.geofences[0]) {
            contentContract.geofence = contract.geofences[0];
        } else if (contract.plannedRides && contract.plannedRides[0]) {
            contentContract.plannedRide = contract.plannedRides[0];
        } else if (contract.trackedRides && contract.trackedRides[0]) {
            contentContract.trackedRide = contract.trackedRides[0];
        } else if (contract.waypoints && contract.waypoints[0]) {
            contentContract.waypoint = contract.waypoints[0];
        }

        return new Content(contentContract);
    }

    static fromUserContentItem(item: UserContentItem): Content {
        const contentContract: LinkedContentObjectContract = {};

        if (item instanceof TrackedRide) {
            contentContract.trackedRide = item.toContract();
        } else if (item instanceof PlannedRide) {
            contentContract.plannedRide = item.toContract();
        } else if (item instanceof Waypoint) {
            contentContract.waypoint = item.toContract();
        } else if (item instanceof Geofence) {
            contentContract.geofence = item.toContract();
        } else if (item instanceof Collection) {
            contentContract.collection = item.toContract();
        } else if (item instanceof RideEvent) {
            contentContract.event = item.toContract();
        }

        return new Content(contentContract);
    }

    clone(): Content {
        return {} as Content;
        // return new Content(this.toContract(), this._contentSeedFactory);
    }

    toContract(): LinkedContentObjectContract {
        return cloneDeep(this.contract);
    }
}
